import { createV3Queries } from "@query/utils/createV3Queries";
import { queriesPayeeEngagements } from "./queriesPayeeEngagements";
import { payeesApi } from "@api";
import { queriesPayeeRows } from "./queriesPayeeRows";
import { QUERY_PAYEE } from "../../query/payee/keys";
import { QUERY_PAYEE_ROWS } from "../../query/search/payee/queries/useQueryPayeeRows";
import { QUERY_PAYEE_ROWS_SUMMARY } from "../../query/search/payee/queries/useQueryPayeeRowsSummary";

/**
 * Cross-resource invalidation for the v3 payee write endpoints during v1→v3
 * coexistence. The name-derived defaults already refresh the v3 payee reads
 * (`getPayee` / `listPayees` / `searchPayees`), but the payee **detail screen**
 * still reads v1 `QUERY_PAYEE`, some screens read the search-service
 * `QUERY_PAYEE_ROWS`, and the engagement lists are v1 — so a v3 payee write must
 * also refresh those v1 caches or those screens go stale after an edit. The v3
 * contractor rows (`payeeRowsApi` — a different registry, so not covered by the
 * name-derived defaults) are refreshed here too. Declared here (not per call
 * site) per the registry-config dependency convention.
 *
 * TODO(v3): remove once the payee detail screen + contractor table read v3.
 */
const v1PayeeReadDependencies = () => [
  QUERY_PAYEE_ROWS,
  QUERY_PAYEE_ROWS_SUMMARY,
  queriesPayeeRows.searchPayeeRows.queryKeyBase,
  queriesPayeeEngagements.getPayeeEngagement.queryKeyBase,
  queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase,
  queriesPayeeEngagements.listPayeeEngagements.queryKeyBase
];

export const queriesPayees = createV3Queries("payeesApi", payeesApi, {
  updatePayee: () => ({
    dependencies: v1PayeeReadDependencies,
    awaitDependencies: (_res, req) => [[QUERY_PAYEE, req.payeeId]]
  }),
  updatePayeeCustomFields: () => ({
    dependencies: v1PayeeReadDependencies,
    awaitDependencies: (_res, req) => [[QUERY_PAYEE, req.payeeId]]
  }),
  // Archive = v3 soft-deactivate (`DELETE /payees/{id}` → `Deactivated`). Same v1
  // read refresh as the updates: the payee detail screen + contractor table still
  // read v1.
  deletePayee: () => ({
    dependencies: v1PayeeReadDependencies,
    awaitDependencies: (_res, req) => [[QUERY_PAYEE, req.payeeId]]
  }),
  // Restore = v3 re-activate (`activatePayee`, the inverse of deletePayee's
  // soft-deactivate: Deactivated → Activated). Same v1 read refresh as the
  // other payee writes.
  activatePayee: () => ({
    dependencies: v1PayeeReadDependencies,
    awaitDependencies: (_res, req) => [[QUERY_PAYEE, req.payeeId]]
  })
});
